import React, { useState } from 'react';
import { IconButton } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import ButtonUI from './ButtonUI';
import DataGridUI from './Datagrid/DataGridUI';
import { DataGridIdentifiers } from './Datagrid/DataGridIdentifiers';
import FileUploadDialogUI from './FileUploadDialogUI';
import { dataGridColumns } from '../../utils/helper';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { render } from '@testing-library/react';
import dayjs from 'dayjs';
import { ActiveColumnEnabledTypes, ParentObjTypes } from '../../config/fileTab';
import CheckboxUI from './CheckboxUI';
import { useFormikContext } from 'formik';
import { FormTypeAttachment } from '../../config/form';

const FileTabUI = ({
  handleUpload,
  disabled,
  savedFiles,
  handleDelete,
  parentObj,
  showUpdatedAt,
  form,
  additionalColumns = [],
  ...props
}) => {
  const formik = useFormikContext();
  const { setFieldValue, values } = formik ?? {};
  const [uploadDialog, setUploadDialog] = useState(false);

  const handleClick = () => {
    setUploadDialog(true);
  };
  const handleClose = () => {
    setUploadDialog(false);
  };

  const handleDownload = async (data) => {
    let filename = data?.name || 'download';

    if (data instanceof File) {
      const url = URL.createObjectURL(data);
      triggerDownload(url, filename);
      URL.revokeObjectURL(url);
      return;
    }

    if (data?.path) {
      const url = `${process.env.REACT_APP_API_URL}/${data.path}`;
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      triggerDownload(blobUrl, filename);
      URL.revokeObjectURL(blobUrl);
    }
  };

  const triggerDownload = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handlePreview = (data) => {
    if (data instanceof File) {
      const url = URL.createObjectURL(data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }
    if (data?.path) {
      const url = `${process.env.REACT_APP_API_URL}/${data.path}`;
      window.open(url, '_blank');
    }
  };

  const getValueIndex = (values, id) => {
    return values.findIndex((rc) => rc.id === id);
  };

  const columns = dataGridColumns([
    {
      field: 'name',
      headerName: 'Melléklet neve',
      flex: 1,
      sortable: false,
    },
    ...additionalColumns,
    ...(ParentObjTypes.includes(parentObj)
      ? [
          {
            field: 'source',
            headerName: 'Forrás',
            flex: 0.8,
          },
          {
            field: 'sourceType',
            headerName: 'Forrástípus',
            flex: 0.5,
          },
        ]
      : []),
    ...(showUpdatedAt
      ? [
          {
            field: 'updatedAt',
            headerName: 'Módosítás dátuma',
            flex: 0.4,
            renderCell: (params) => {
              return dayjs(params.value).format('YYYY.MM.DD HH:mm');
            },
          },
        ]
      : []),
    {
      field: 'actions',
      headerName: 'Műveletek',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => {
        const ext = (
          params.row?.extension ||
          (params.row?.name ? params.row.name.slice(params.row.name.lastIndexOf('.')) : '') ||
          ''
        ).toLowerCase();

        const enabledExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'];

        const previewable = enabledExtensions.includes(ext);

        return (
          <div>
            {params.row.external ? (
              <IconButton
                onClick={() => {
                  if (params.row.path) {
                    navigator.clipboard.writeText(params.row.path);
                    addNotification({
                      type: NotificationType.INFO,
                      content: 'A fájl elérési útja a vágólapra másolva',
                    });
                  }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            ) : (
              <>
                {previewable && (
                  <IconButton onClick={() => handlePreview(params.row)}>
                    <VisibilityIcon />
                  </IconButton>
                )}

                <IconButton onClick={() => handleDownload(params.row)}>
                  <DownloadIcon />
                </IconButton>

                {handleDelete && (
                  <IconButton
                    disabled={disabled || (params.row?.deletable !== undefined && params.row.deletable !== true)}
                    onClick={() => handleDelete(params.row)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </>
            )}
          </div>
        );
      },
    },
    ...(ActiveColumnEnabledTypes.includes(form) && formik
      ? [
          {
            field: 'active',
            headerName: 'Aktív',
            flex: 0.4,
            renderCell: (params) => {
              const valueIndex = getValueIndex(values?.[FormTypeAttachment[form]], params.row.id);
              return (
                <CheckboxUI
                  disabled={disabled}
                  checked={values?.[FormTypeAttachment[form]]?.[valueIndex]?.active}
                  onChange={(e) => {
                    const fieldName = FormTypeAttachment[form];
                    const nextValue = e.target.checked;

                    if (values?.[fieldName]?.[valueIndex] instanceof File) {
                      const files = [...values[fieldName]];
                      files[valueIndex].active = nextValue;

                      setFieldValue(fieldName, files);
                      return;
                    }

                    setFieldValue(`${fieldName}.${valueIndex}.active`, nextValue);
                  }}
                />
              );
            },
          },
        ]
      : []),
  ]);

  return (
    <>
      {handleUpload && (
        <ButtonUI className="bg-success mb-1" text="Új fájl" disabled={disabled} onClick={handleClick} />
      )}

      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <DataGridUI
              datagridIdentifier={DataGridIdentifiers.uploadFile}
              sx={{ height: 400 }}
              columns={columns}
              rows={savedFiles}
              getRowId={(row) => `${row.path}-${row.id}`}
            />
          </div>
        </div>
      </div>

      <FileUploadDialogUI
        open={uploadDialog}
        handleClose={handleClose}
        handleUpload={(files) => {
          handleUpload(files);
          handleClose();
        }}
        {...props}
      />
    </>
  );
};

export default FileTabUI;
